import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import {
  Box,
  Grid,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  Card,
  Skeleton,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Zoom,
  keyframes
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// Child components
import HomeCardItem from "../Home/HomeCardItem";
import FeedCardItem from "../Home/FeedCardItem";
import ChipList from "../common/ChipList";
import AddToSpace from "../Home/AddToSpace";
import ConfirmationDialog from "../common/ConfirmationDialog";

// Assets & constants
import ai from "../../Assets/ai.png";
import { popupTimer, API_URL } from "../../consts";

/**
 * Component responsible for:
 *  - searching items in a Space
 *  - displaying items (rates, bookmarks, etc.)
 *  - showing recommendations
 */
const SpaceRates = forwardRef(({ spaceId, parentSpaceId, onRemoveItem, onAiFilterApplied, isAiThinking = false, defaultShowRecommendations = false, showRecommendations = false, onShowRecommendationsChange, viewMode = 'grid', spaceData = {} }, ref) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.token);
  const authorized = useSelector((state) => state.authorized);
  const tokenInProcess = useSelector((state) => state.setTokenInProcess);
  const searchState = useSelector((state) => state.search);
  const { id } = useParams();

  const [data, setData] = useState({ rates: [] });
  const [recommendations, setRecommendations] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [isLoadingRates, setIsLoadingRates] = useState(true); // Track initial rates loading
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [isPollingRefresh, setIsPollingRefresh] = useState(false); // Track polling refresh (not initial load)
  const [recommendationsError, setRecommendationsError] = useState(false);
  
  // Add state for AI-filtered rating IDs
  const [aiFilteredRatingIds, setAiFilteredRatingIds] = useState(null);
  const [isAiFiltered, setIsAiFiltered] = useState(false);
  
  // Add pagination states
  const [ratesPage, setRatesPage] = useState(0);
  const [recommendationsPage, setRecommendationsPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreRates, setHasMoreRates] = useState(true);
  const [hasMoreRecommendations, setHasMoreRecommendations] = useState(true);
  const containerRef = useRef(null);
  
  // Refs for polling interval management
  const recommendationsIntervalRef = useRef(null);
  const intervalStartTimeRef = useRef(null);
  
  // Ref to prevent duplicate API calls
  const isRatesRequestInProgress = useRef(false);
  const hasInitialLoadCompleted = useRef(false);

  // Add states for "Add to Space" functionality
  const [openSpaceModal, setOpenSpaceModal] = useState(false);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [selectedItemData, setSelectedItemData] = useState(null);
  
  // Add state for hiding recommendations message
  const [hideRecommendationsMessage, setHideRecommendationsMessage] = useState(false);
  
  // Add state for hiding AI disclaimer
  const [hideAiDisclaimer, setHideAiDisclaimer] = useState(false);
  
  // Add state for delete dialog
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removedRateId, setRemovedRateId] = useState(null);
  
  // Add state for hiding recommendations loading indicator
  const [hideRecommendationsLoading, setHideRecommendationsLoading] = useState(false);
  
  // View mode is now controlled by parent component

  const getRatesTrigger = useSelector((state) => state.getRatesTrigger);

  // Mock data for recommendations
  const mockRecommendations = {
    rates: [
      {
        _id: "mock1",
        title: "Sample Recommendation 1",
        description: "This is a sample recommendation item",
        image_url: "https://via.placeholder.com/300x200",
        tags: ["sample", "recommendation"]
      },
      {
        _id: "mock2",
        title: "Sample Recommendation 2",
        description: "Another sample recommendation item",
        image_url: "https://via.placeholder.com/300x200",
        tags: ["sample", "recommendation"]
      },
      {
        _id: "mock3",
        title: "Sample Recommendation 3",
        description: "Yet another sample recommendation",
        image_url: "https://via.placeholder.com/300x200",
        tags: ["sample", "recommendation"]
      }
    ]
  };

  // ------------------------------------------------------
  // Fetch Rates from the space, with or without token
  // ------------------------------------------------------
  const getRatesRequest = useCallback((page = 0, append = false, ratingIds = null) => {
    if (!spaceId) return;
    
    // Prevent duplicate calls for initial load
    if (!append && isRatesRequestInProgress.current) {
      return;
    }
    
    // Set loading state only for initial load (not for pagination)
    if (!append) {
      isRatesRequestInProgress.current = true;
      setIsLoadingRates(true);
    }

    const paramsBody = {
      start_from: page * 10,
      size: 10
    };
    
    // AI filter IDs are rated-object IDs — send as object_ids so the backend can
    // look up the matching ratings within this space
    if (ratingIds && Array.isArray(ratingIds) && ratingIds.length > 0) {
      paramsBody.object_ids = ratingIds.join(",");
    } else if (aiFilteredRatingIds && Array.isArray(aiFilteredRatingIds) && aiFilteredRatingIds.length > 0) {
      paramsBody.object_ids = aiFilteredRatingIds.join(",");
    } else if (searchState.tags.length > 0) {
      paramsBody.tags = searchState.tags.join(",");
    }

    let url = "";
    if (!token) {
      url = `${API_URL}/spaces/${spaceId}/rates`;
    } else {
      url = `${API_URL}/spaces/authenticated/${spaceId}/rates`;
    }
    

    axios
      .get(url, { params: paramsBody })
      .then((res) => {
        const rates = res.data.rates || [];
        if (append) {
          setData(prev => ({
            ...prev,
            rates: [...prev.rates, ...rates]
          }));
        } else {
          setData({ rates });
        }
        // If we got less than 10 items, there are no more
        setHasMoreRates(rates.length === 10);
        setIsLoadingMore(false);
        setIsLoadingRates(false);
        if (!append) {
          isRatesRequestInProgress.current = false;
          hasInitialLoadCompleted.current = true;
        }
      })
      .catch((err) => {
        console.error('Rates API error:', err);
        setIsLoadingMore(false);
        setIsLoadingRates(false);
        setHasMoreRates(false);
        if (!append) {
          isRatesRequestInProgress.current = false;
          hasInitialLoadCompleted.current = true; // Mark as completed even on error to prevent retry loops
        }
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
      });
  }, [spaceId, searchState.tags, token, dispatch, aiFilteredRatingIds]);

  // Fetch recommendations with pagination
  const getRecommendations = useCallback((page = 0, append = false) => {
    if (!spaceId || !token) return Promise.resolve();
    
    setIsLoadingRecommendations(!append);
    setRecommendationsError(false);
    
    const url = `${API_URL}/spaces/authenticated/${spaceId}/recommendations`;
    const params = {
      start_from: page * 10,
      size: 10
    };
    
    return axios
      .get(url, { params })
      .then((res) => {
        const rates = res.data.rates || [];
        if (append) {
          setRecommendations(prev => [...prev, ...rates]);
        } else {
          setRecommendations(rates);
        }
        // If we got less than 10 items, there are no more
        setHasMoreRecommendations(rates.length === 10);
        setIsLoadingMore(false);
        setIsLoadingRecommendations(false);
      })
      .catch((err) => {
        console.error('Recommendations API error:', err);
        setIsLoadingMore(false);
        setIsLoadingRecommendations(false);
        setHasMoreRecommendations(false);
        setRecommendationsError(true);
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
      });
  }, [spaceId, token, dispatch]);

  // Add recommended item to space
  const addRecommendedItem = (rateId) => {
    const url = `${API_URL}/spaces/${spaceId}/rates`;
    axios
      .put(url, { rate_id: rateId })
      .then(() => {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Item added successfully",
            type: "success",
          },
        });
        // Refresh both lists
        getRatesRequest();
        getRecommendations();
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
      });
  };

  // Handle "Add to Space" functionality
  const showSpacePopup = (rateId, itemData = null) => {
    if (authorized) {
      // For recommendations, rateId might be the rating ID, but we need the object_id
      // Extract the actual object_id from itemData if available
      const actualObjectId = itemData?.rated_object_id || itemData?.object_id
      setSelectedRateId(actualObjectId);
      setSelectedItemData(itemData);
      setOpenSpaceModal(true);
    } else {
      dispatch({
        type: "SET_POPUP",
        payload: {
          text: "You must Login to Perform this Action",
          type: "error",
        },
      });
      setTimeout(() => {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "",
            type: "",
          },
        });
      }, popupTimer);
    }
  };

  const handleSpaceModalClose = () => {
    setOpenSpaceModal(false);
    setSelectedRateId(null);
    setSelectedItemData(null);
  };

  // New function to handle when the process is truly completed
  const handleSpaceModalComplete = () => {
    setOpenSpaceModal(false);
    setSelectedRateId(null);
    setSelectedItemData(null);
  };

  // Handle scroll event
  const handleScroll = useCallback(() => {
    if (!containerRef.current || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isNearBottom = scrollHeight - scrollTop <= clientHeight * 1.5;

    if (isNearBottom) {
      if (!showRecommendations && hasMoreRates && !isLoadingMore) {
        setIsLoadingMore(true);
        const nextPage = ratesPage + 1;
        setRatesPage(nextPage);
        getRatesRequest(nextPage, true);
      } else if (showRecommendations && hasMoreRecommendations && !isLoadingMore) {
        setIsLoadingMore(true);
        const nextPage = recommendationsPage + 1;
        setRecommendationsPage(nextPage);
        getRecommendations(nextPage, true);
      }
    }
  }, [showRecommendations, hasMoreRates, hasMoreRecommendations, isLoadingMore, ratesPage, recommendationsPage, getRatesRequest, getRecommendations]);

  // Add scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);


  // Handle showRecommendations toggle - reset pagination and fetch appropriate data
  useEffect(() => {
    setRatesPage(0);
    setRecommendationsPage(0);
    setHasMoreRates(true);
    setHasMoreRecommendations(true);
    setIsLoadingMore(false);

    if (showRecommendations) {
      setData({ rates: [] });
      setIsLoadingRates(false);
      if (!tokenInProcess) {
        getRecommendations();
      }
    } else {
      setRecommendations([]);
      setIsLoadingRecommendations(false);
      // Fetch rates when switching to saved items view
      if (!tokenInProcess && spaceId) {
        isRatesRequestInProgress.current = false; // Reset the flag
        hasInitialLoadCompleted.current = false; // Reset initial load flag
        getRatesRequest(0, false, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecommendations]); // Only depend on showRecommendations to avoid duplicate calls

  // Track spaceId changes to reset initial load flag
  const prevSpaceIdRef = useRef(spaceId);
  
  // Main effect: Fetch rates when spaceId, token, tags change (only when showing saved items)
  // This effect handles data refreshes, not view toggles or initial load
  useEffect(() => {
    // Reset initial load flag if spaceId changed
    if (prevSpaceIdRef.current !== spaceId) {
      hasInitialLoadCompleted.current = false;
      prevSpaceIdRef.current = spaceId;
    }
    
    if (tokenInProcess || showRecommendations || getRatesTrigger) {
      return; // Skip if token is processing, showing recommendations, or getRatesTrigger is active
    }
    
    if (!spaceId) return;
    
    // Skip initial load - let the showRecommendations effect handle it
    // Only handle refreshes when dependencies change after initial load
    if (!hasInitialLoadCompleted.current) {
      return; // Let the other effect handle initial load
    }
    
    // Reset pagination for new fetch
    setRatesPage(0);
    setHasMoreRates(true);
    isRatesRequestInProgress.current = false; // Reset the flag for refresh
    
    getRatesRequest(0, false, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceId, tokenInProcess, searchState.tags]); // Removed showRecommendations and getRatesTrigger to avoid conflicts

  // Handle getRatesTrigger - explicit refresh trigger
  useEffect(() => {
    if (getRatesTrigger) {
      setRatesPage(0);
      setHasMoreRates(true);
      isRatesRequestInProgress.current = false; // Reset the flag
      hasInitialLoadCompleted.current = true; // Mark as completed since this is an explicit refresh
      
      getRatesRequest(0, false, null);
      if (showRecommendations) {
        getRecommendations(0, false);
      }
      dispatch({ type: "GET_RATES", payload: false });
    }
  }, [getRatesTrigger, getRatesRequest, getRecommendations, showRecommendations, dispatch]);

  // Polling interval for recommendations (every 30 seconds, max 10 minutes)
  useEffect(() => {
    // Clear any existing interval first
    if (recommendationsIntervalRef.current) {
      clearInterval(recommendationsIntervalRef.current);
      recommendationsIntervalRef.current = null;
    }
    intervalStartTimeRef.current = null;
    setIsPollingRefresh(false);

    // Set up polling if conditions are met
    if (defaultShowRecommendations && showRecommendations && token && spaceId) {
      // Record start time
      intervalStartTimeRef.current = Date.now();
      
      // Set up interval to fetch recommendations every 30 seconds
      recommendationsIntervalRef.current = setInterval(() => {
        // Check if 10 minutes (600000 ms) have passed
        const elapsed = Date.now() - intervalStartTimeRef.current;
        if (elapsed >= 600000) {
          // Stop polling after 10 minutes
          if (recommendationsIntervalRef.current) {
            clearInterval(recommendationsIntervalRef.current);
            recommendationsIntervalRef.current = null;
          }
          intervalStartTimeRef.current = null;
          setIsPollingRefresh(false);
          return;
        }

        // Set polling refresh state and fetch recommendations
        setIsPollingRefresh(true);
        getRecommendations(0, false).finally(() => {
          setIsPollingRefresh(false);
        });
      }, 30000); // 30 seconds

    }

    // Cleanup function
    return () => {
      if (recommendationsIntervalRef.current) {
        clearInterval(recommendationsIntervalRef.current);
        recommendationsIntervalRef.current = null;
      }
      intervalStartTimeRef.current = null;
      setIsPollingRefresh(false);
    };
  }, [defaultShowRecommendations, showRecommendations, token, spaceId, getRecommendations]);

  // Add console logging for data state
  useEffect(() => {
  }, [data, recommendations]);

  const handleRemoveFromSpace = (rateId) => {
    if (!spaceId) return;
    const url = `${API_URL}/spaces/${spaceId}/remove-item`;
    // Optimistically remove the item, keeping a snapshot to roll back on failure
    const previousData = data;
    setData((prev) => ({
      ...prev,
      rates: (prev.rates || []).filter(
        (item) => item._id !== rateId && item.id !== rateId && item.object_id !== rateId
      ),
    }));
    axios
      .post(url, { rate_id: rateId })
      .then(() => {
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Item removed",
            type: "success",
          },
        });
        // Refresh
        getRatesRequest();
        getRecommendations();
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      })
      .catch((err) => {
        // Roll back the optimistic removal on failure
        setData(previousData);
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to remove item",
            type: "error",
          },
        });
        setTimeout(() => {
          dispatch({ type: "SET_POPUP", payload: { text: "", type: "" } });
        }, popupTimer);
      });
  };

  const deleteRate = () => {
    if (!removedRateId) return;
    const rateId = removedRateId;
    const url = `${API_URL}/rates/${rateId}/delete`;
    // Optimistically remove the item, keeping a snapshot to roll back on failure
    const previousData = data;
    setData((prev) => ({
      ...prev,
      rates: (prev.rates || []).filter(
        (item) => item._id !== rateId && item.id !== rateId
      ),
    }));
    axios
      .post(url, null)
      .then((res) => {
        // Refresh rates
        getRatesRequest();
        getRecommendations();
        setTimeout(() => {
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: "",
              type: "",
            },
          });
        }, popupTimer);
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: "Rate deleted successfully",
            type: "success",
          },
        });
      })
      .catch((err) => {
        // Roll back the optimistic removal on failure
        setData(previousData);
        if (err?.response?.status === 401) {
          dispatch({ type: "SET_UNAUTHORIZED" });
          return;
        }
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: err?.response?.data?.error || "Failed to delete rate",
            type: "error",
          },
        });
      });
  };

  const handleItemSelect = (item) => {
    setSelectedItems(prev => {
      const isAlreadySelected = prev.some(i => i._id === item._id);
      if (isAlreadySelected) {
        return prev.filter(i => i._id !== item._id);
      }
      return [...prev, item];
    });
  };

  const handleCompare = () => {
    setCompareDialogOpen(true);
  };

  const handleCloseCompare = () => {
    setCompareDialogOpen(false);
    setSelectedItems([]);
  };

  // Removed tabs; using checkbox toggle instead
  // Removed view mode change handler - now controlled by parent

  // Handle AI filter response — items are rated-object IDs (strings) from /chats/ai
  const handleAiFilterResponse = useCallback((items) => {
    if (items && Array.isArray(items) && items.length > 0) {
      setAiFilteredRatingIds(items);
      setIsAiFiltered(true);
      setRatesPage(0);
      setHasMoreRates(true);
      getRatesRequest(0, false, items);
      if (onAiFilterApplied) onAiFilterApplied(items);
    } else {
      // Clear AI filter
      setAiFilteredRatingIds(null);
      setIsAiFiltered(false);
      setRatesPage(0);
      setHasMoreRates(true);
      getRatesRequest(0, false, null);
      if (onAiFilterApplied) onAiFilterApplied(null);
    }
  }, [getRatesRequest, onAiFilterApplied]);

  // Clear AI filter
  const clearAiFilter = useCallback(() => {
    handleAiFilterResponse(null);
  }, [handleAiFilterResponse]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    applyAiFilter: handleAiFilterResponse,
    clearAiFilter,
    refreshRates: () => getRatesRequest(0, false, null)
  }), [handleAiFilterResponse, clearAiFilter, getRatesRequest]);

  const ComparisonDialog = () => (
    <Dialog 
      open={compareDialogOpen} 
      onClose={handleCloseCompare}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Compare Items</Typography>
          <IconButton onClick={handleCloseCompare}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Property</TableCell>
                {selectedItems.map((item, index) => (
                  <TableCell key={index}>{item.title || `Item ${index + 1}`}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedItems[0] && Object.keys(selectedItems[0]).map((key) => (
                <TableRow key={key}>
                  <TableCell component="th" scope="row">
                    {key}
                  </TableCell>
                  {selectedItems.map((item, index) => (
                    <TableCell key={index}>
                      {typeof item[key] === 'object' 
                        ? JSON.stringify(item[key])
                        : item[key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );

  // Define keyframes for AI thinking animations
  const floatAnimation = keyframes`
    0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
    25% { transform: translateY(-10px) rotate(2deg) scale(1.02); }
    50% { transform: translateY(-5px) rotate(-1deg) scale(1.01); }
    75% { transform: translateY(-15px) rotate(1deg) scale(1.03); }
  `;

  const pulseGlow = keyframes`
    0%, 100% { 
      box-shadow: 0 4px 20px rgba(102, 126, 234, 0.2), 
                  0 0 0 0 rgba(102, 126, 234, 0.4);
    }
    50% { 
      box-shadow: 0 8px 40px rgba(102, 126, 234, 0.4), 
                  0 0 0 10px rgba(102, 126, 234, 0.1);
    }
  `;

  const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  `;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)',
      pt: 2,
      px: { xs: 1, sm: 2, md: 3 }
    }}>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          
          {/* AI Thinking Indicator */}
          {isAiThinking && (
            <Fade in={isAiThinking} timeout={500}>
              <Box 
                sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 3,
                  background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))',
                  border: '2px solid rgba(102, 126, 234, 0.3)',
                  backdropFilter: 'blur(10px)',
                  animation: `${pulseGlow} 2s ease-in-out infinite`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-200%',
                    width: '200%',
                    height: '100%',
                    background: `linear-gradient(90deg, 
                      transparent, 
                      rgba(255,255,255,0.4), 
                      transparent)`,
                    animation: `${shimmer} 2s infinite linear`
                  }
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: `${floatAnimation} 3s ease-in-out infinite`,
                      position: 'relative',
                      zIndex: 1
                    }}
                  >
                    <img src={ai} alt="AI" style={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }} />
                  </Box>
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      color: '#667eea',
                      mb: 0.5
                    }}>
                      AI is analyzing your data...
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: '#6b7280',
                      opacity: 0.8
                    }}>
                      Watch the magic happen as we process your request ✨
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Fade>
          )}
          {/* Compare Button Row - only shown when items are selected */}
          {selectedItems.length > 0 && (
            <Box display="flex" alignItems="center" mb={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CompareArrowsIcon />}
                onClick={handleCompare}
              >
                Compare ({selectedItems.length})
              </Button>
            </Box>
          )}

          {/* AI Filter Indicator - shown when AI filtering is active */}
          {isAiFiltered && (
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} px={2} py={1} sx={{
              background: 'rgba(99, 102, 241, 0.07)',
              border: '1px solid rgba(99, 102, 241, 0.18)',
              borderRadius: 2,
            }}>
              <Box display="flex" alignItems="center" gap={1}>
                <img src={ai} alt="AI" style={{ width: 18, height: 18 }} />
                <Typography variant="body2" sx={{ color: '#6366f1', fontWeight: 500 }}>
                  AI filter active &mdash; {aiFilteredRatingIds?.length || 0} result{(aiFilteredRatingIds?.length || 0) !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={clearAiFilter}
                sx={{ color: '#6366f1', textTransform: 'none', fontWeight: 500, fontSize: '0.8rem', minWidth: 0 }}
              >
                Clear filter
              </Button>
            </Box>
          )}

          {/* Recommendations checkbox */}
          {/* Tab-based interface - no checkbox needed */}

      {/* Recommendations Loading Indicator - shows when waiting for recommendations */}
      {defaultShowRecommendations && showRecommendations && !hideRecommendationsLoading && (
        <Box 
          sx={{ 
            mb: 2, 
            p: 1.5, 
            backgroundColor: 'rgba(102, 126, 234, 0.08)', 
            borderRadius: 1.5,
            border: '1px solid rgba(102, 126, 234, 0.25)',
            mx: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            position: 'relative'
          }}
        >
          <CircularProgress size={20} sx={{ color: '#667eea' }} />
          <Typography
            variant="body2"
            sx={{ color: '#667eea', fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1.4, pr: 3 }}
          >
            Collecting the best recommendations for you...
          </Typography>
          <IconButton
            size="small"
            onClick={() => setHideRecommendationsLoading(true)}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              color: '#667eea',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.1)'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Combined Recommendations Info Message */}
      {defaultShowRecommendations && showRecommendations && !hideRecommendationsMessage && (
        <Box 
          sx={{ 
            mb: 2, 
            p: 1.5, 
            backgroundColor: 'rgba(76, 175, 80, 0.08)', 
            borderRadius: 1.5,
            border: '1px solid rgba(76, 175, 80, 0.25)',
            mx: 1,
            position: 'relative'
          }}
        >
          <IconButton
            size="small"
            onClick={() => setHideRecommendationsMessage(true)}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              color: '#2e7d32',
              '&:hover': {
                backgroundColor: 'rgba(46, 125, 50, 0.1)'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="body2"
            sx={{ color: '#2e7d32', fontWeight: 500, pr: 3, fontSize: '0.8125rem', lineHeight: 1.4 }}
          >
            💡 Recommendations are active in this collection. Fetching may take a moment — you can check back later for updates.
            <br />
            ⚠️ AI-generated recommendations - please validate before use.
            <br />
            📊 Only up to 10 recommendations are displayed. To see more, add them to your collection or remove existing ones.
          </Typography>
        </Box>
      )}

          <Grid 
            container 
            spacing={viewMode === 'feed' ? 0 : 1} 
            style={{ paddingTop: "8px", width: "100%" }}
            ref={containerRef}
            sx={{ 
              flex: 1,
              overflowY: 'auto',
              backgroundColor: viewMode === 'feed' ? 'linear-gradient(135deg, rgba(248,250,252,0.6) 0%, rgba(255,255,255,0.8) 100%)' : 'transparent',
              backdropFilter: viewMode === 'feed' ? 'blur(10px)' : 'none',
              minHeight: viewMode === 'feed' ? '100vh' : 'auto',
              borderRadius: viewMode === 'feed' ? '20px' : 0,
              paddingBottom: 1,
              p: 1,
              width: '100%',
              margin: 0,
              border: viewMode === 'feed' ? '1px solid rgba(226,232,240,0.6)' : 'none',
              boxShadow: viewMode === 'feed' ? '0 8px 32px rgba(0,0,0,0.08)' : 'none',
              // Custom scrollbar
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(226,232,240,0.3)',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                borderRadius: '10px',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)',
                }
              },
            }}
          >
        {/* Loading skeleton for initial rates load */}
        {isLoadingRates && !showRecommendations && (
          [...Array(viewMode === 'feed' ? 3 : 6)].map((_, index) => (
            <Grid 
              item 
              xs={viewMode === 'feed' ? 12 : 12} 
              sm={viewMode === 'feed' ? 12 : 6} 
              md={viewMode === 'feed' ? 12 : 4} 
              key={`skeleton-rates-${index}`}
              sx={{ 
                display: 'flex',
                minHeight: viewMode === 'grid' ? '350px' : 'auto'
              }}
            >
              <Paper 
                sx={{ 
                  height: viewMode === 'feed' ? 'auto' : '330px', 
                  marginBottom: '10px', 
                  padding: '16px',
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  width: '100%'
                }}
              >
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
                <Skeleton variant="text" height={28} width="80%" sx={{ mb: 1 }} />
                <Skeleton variant="text" height={20} width="60%" sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Skeleton variant="rectangular" height={36} width={100} sx={{ borderRadius: 4 }} />
                  <Skeleton variant="rectangular" height={36} width={100} sx={{ borderRadius: 4 }} />
                </Box>
              </Paper>
            </Grid>
          ))
        )}

        {/* Always show saved items first */}
        {!isLoadingRates && data && data.rates && data.rates.length > 0 ? (
          <>
            {data.rates.map((item, index) => (
            <Grid 
              item 
              xs={viewMode === 'feed' ? 12 : 12} 
              sm={viewMode === 'feed' ? 12 : 6} 
              md={viewMode === 'feed' ? 12 : 4} 
              key={item._id}
              sx={{ 
                display: 'flex',
                minHeight: viewMode === 'grid' ? '350px' : 'auto'
              }}
            >
              <div style={{ 
                height: viewMode === 'feed' ? '100%' : '100%',
                maxWidth: viewMode === 'feed' ? '600px' : 'auto',
                margin: viewMode === 'feed' ? '0 auto' : 'auto',
                minHeight: viewMode === 'grid' ? '330px' : 'auto',
                display: 'flex',
                width: '100%',
                marginBottom: viewMode === 'feed' ? '0' : '10px',
                padding: viewMode === 'feed' ? '0' : '10px',
                // AI thinking animations
                ...(isAiThinking && {
                  animation: `${floatAnimation} ${3 + (index * 0.2)}s ease-in-out infinite`,
                  animationDelay: `${index * 0.1}s`,
                  transform: 'translateZ(0)', // Force hardware acceleration
                  filter: 'brightness(1.1) saturate(1.2)',
                  transition: 'all 0.3s ease'
                })
              }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    ...(isAiThinking && {
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(45deg, 
                          transparent 30%, 
                          rgba(102, 126, 234, 0.1) 50%, 
                          transparent 70%)`,
                        borderRadius: '12px',
                        animation: `${shimmer} 3s infinite linear`,
                        pointerEvents: 'none',
                        zIndex: 1
                      },
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: '20px',
                        height: '20px',
                        background: 'linear-gradient(45deg, #667eea, #764ba2)',
                        borderRadius: '50%',
                        transform: 'translate(-50%, -50%)',
                        animation: `${pulseGlow} 2s ease-in-out infinite`,
                        opacity: 0.7,
                        pointerEvents: 'none',
                        zIndex: 2
                      }
                    })
                  }}
                >
                  {viewMode === 'grid' ? (
                    <HomeCardItem
                      isBookmark
                      data={item}
                      searhTags={searchState.tags}
                      applyMargin
                      displayRemoveFromSpace
                      onRemoveFromSpaceClick={handleRemoveFromSpace}
                      onDelete={(id) => {
                        setRemoveDialogOpen(true);
                        setRemovedRateId(id);
                      }}
                      isSelected={selectedItems.some(i => i._id === item._id)}
                      onSelect={() => handleItemSelect(item)}
                      showSpacePopup={() => showSpacePopup(item.object_id, item)}
                      currentSpaceId={spaceId}
                      parentSpaceId={parentSpaceId}
                      enableDragAndDrop={true}
                      isCommunity={spaceData?.is_community || false}
                      onVisibilityUpdate={() => {
                        // Refresh rates after visibility update
                        getRatesRequest(0, false, null);
                      }}
                      style={{
                        ...(isAiThinking && {
                          opacity: 0.8,
                          transition: 'all 0.3s ease'
                        })
                      }}
                    />
                  ) : (
                    <FeedCardItem
                      isBookmark
                      data={item}
                      searhTags={searchState.tags}
                      applyMargin
                      displayRemoveFromSpace
                      onRemoveFromSpaceClick={handleRemoveFromSpace}
                      onDelete={(id) => {
                        setRemoveDialogOpen(true);
                        setRemovedRateId(id);
                      }}
                      isSelected={selectedItems.some(i => i._id === item._id)}
                      onSelect={() => handleItemSelect(item)}
                      showSpacePopup={(rateId) => showSpacePopup(rateId, item)}
                      currentSpaceId={spaceId}
                      parentSpaceId={parentSpaceId}
                      enableDragAndDrop={true}
                      isCommunity={spaceData?.is_community || false}
                      onVisibilityUpdate={() => {
                        // Refresh rates after visibility update
                        getRatesRequest(0, false, null);
                      }}
                      style={{
                        ...(isAiThinking && {
                          opacity: 0.8,
                          transition: 'all 0.3s ease'
                        })
                      }}
                    />
                  )}
                </Box>
              </div>
            </Grid>
          ))}
          {!showRecommendations && isLoadingMore && (
            <Grid item xs={12}>
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            </Grid>
          )}
          </>
        ) : null}

        {/* Show recommendations below saved items when enabled */}
        {showRecommendations && (
          <>
            {isLoadingRecommendations && !isLoadingMore ? (
              // Loading state
              [...Array(viewMode === 'feed' ? 2 : 3)].map((_, index) => (
              <Grid 
                item 
                xs={viewMode === 'feed' ? 12 : 12} 
                sm={viewMode === 'feed' ? 12 : 6} 
                md={viewMode === 'feed' ? 12 : 4} 
                key={`skeleton-${index}`}
                sx={{ 
                  display: 'flex',
                  minHeight: viewMode === 'grid' ? '350px' : 'auto'
                }}
              >
                <Paper 
                  sx={{ 
                    height: viewMode === 'feed' ? 'auto' : '330px', 
                    marginBottom: '10px', 
                    padding: '16px',
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    width: '100%'
                  }}
                >
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
                  <Skeleton variant="text" height={28} width="80%" sx={{ mb: 1 }} />
                  <Skeleton variant="text" height={20} width="60%" sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                    <Skeleton variant="rectangular" height={36} width={100} sx={{ borderRadius: 4 }} />
                    <Skeleton variant="rectangular" height={36} width={100} sx={{ borderRadius: 4 }} />
                  </Box>
                </Paper>
              </Grid>
            ))
          ) : recommendationsError ? (
            // Error state
            <Grid item xs={12}>
              <Typography color="error">
                Failed to load recommendations. Please try again later.
              </Typography>
            </Grid>
          ) : (
            // Recommendations content
            Array.isArray(recommendations) && recommendations.length > 0 && (
              <>
                {recommendations.map((item, index) => (
                  <Grid 
                    item 
                    xs={viewMode === 'feed' ? 12 : 12} 
                    sm={viewMode === 'feed' ? 12 : 6} 
                    md={viewMode === 'feed' ? 12 : 4} 
                    key={item._id}
                    sx={{ 
                      display: 'flex',
                      minHeight: viewMode === 'grid' ? '350px' : 'auto'
                    }}
                  >
                    <div style={{ 
                      height: viewMode === 'feed' ? '100%' : '100%',
                      maxWidth: viewMode === 'feed' ? '600px' : 'auto',
                      margin: viewMode === 'feed' ? '0 auto' : 'auto',
                      minHeight: viewMode === 'grid' ? '330px' : 'auto',
                      display: 'flex',
                      width: '100%',
                      marginBottom: viewMode === 'feed' ? '0' : '10px',
                      padding: viewMode === 'feed' ? '0' : '10px',
                      // AI thinking animations
                      ...(isAiThinking && {
                        animation: `${floatAnimation} ${3 + (index * 0.2)}s ease-in-out infinite`,
                        animationDelay: `${index * 0.1}s`,
                        transform: 'translateZ(0)', // Force hardware acceleration
                        filter: 'brightness(1.1) saturate(1.2)',
                        transition: 'all 0.3s ease'
                      })
                    }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          ...(isAiThinking && {
                            position: 'relative',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: `linear-gradient(45deg, 
                                transparent 30%, 
                                rgba(102, 126, 234, 0.1) 50%, 
                                transparent 70%)`,
                              borderRadius: '12px',
                              animation: `${shimmer} 3s infinite linear`,
                              pointerEvents: 'none',
                              zIndex: 1
                            },
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              width: '20px',
                              height: '20px',
                              background: 'linear-gradient(45deg, #667eea, #764ba2)',
                              borderRadius: '50%',
                              transform: 'translate(-50%, -50%)',
                              animation: `${pulseGlow} 2s ease-in-out infinite`,
                              opacity: 0.7,
                              pointerEvents: 'none',
                              zIndex: 2
                            }
                          })
                        }}
                      >
                        {viewMode === 'grid' ? (
                          <HomeCardItem
                            isBookmark
                            data={item}
                            searhTags={searchState.tags}
                            applyMargin
                            displayAddToSpace
                            onAddToSpaceClick={() => showSpacePopup(item.object_id, item)}
                            isSelected={selectedItems.some(i => i._id === item._id)}
                            onSelect={() => handleItemSelect(item)}
                            showSpacePopup={(rateId) => showSpacePopup(item.rated_object_id || item.object_id || rateId, item)}
                            currentSpaceId={spaceId}
                            parentSpaceId={parentSpaceId}
                            enableDragAndDrop={true}
                            isCommunity={spaceData?.is_community || false}
                            onVisibilityUpdate={() => {
                              // Refresh recommendations after visibility update
                              getRecommendations(0, false);
                            }}
                            style={{
                              ...(isAiThinking && {
                                opacity: 0.8,
                                transition: 'all 0.3s ease'
                              })
                            }}
                          />
                        ) : (
                          <FeedCardItem
                            isBookmark
                            data={item}
                            searhTags={searchState.tags}
                            applyMargin
                            displayAddToSpace
                            onAddToSpaceClick={() => showSpacePopup(item.rated_object_id || item.object_id || item._id, item)}
                            isSelected={selectedItems.some(i => i._id === item._id)}
                            onSelect={() => handleItemSelect(item)}
                            showSpacePopup={(rateId) => showSpacePopup(item.rated_object_id || item.object_id || rateId, item)}
                            currentSpaceId={spaceId}
                            parentSpaceId={parentSpaceId}
                            enableDragAndDrop={true}
                            isCommunity={spaceData?.is_community || false}
                            onVisibilityUpdate={() => {
                              // Refresh recommendations after visibility update
                              getRecommendations(0, false);
                            }}
                            style={{
                              ...(isAiThinking && {
                                opacity: 0.8,
                                transition: 'all 0.3s ease'
                              })
                            }}
                          />
                        )}
                      </Box>
                    </div>
                  </Grid>
                ))}
                {isLoadingMore && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress />
                    </Box>
                  </Grid>
                )}
              </>
            )
            )}
          </>
        )}

        {/* Empty collection message - shown when no saved items AND not loading */}
        {!isLoadingRates && !isLoadingRecommendations && (!data || !data.rates || data.rates.length === 0) && !showRecommendations && (
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)',
              borderRadius: 3,
              border: '2px dashed rgba(156, 163, 175, 0.3)',
              mx: 2
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <ViewModuleIcon sx={{ color: 'white', fontSize: 40 }} />
              </Box>
              
              <Typography variant="h5" sx={{
                fontWeight: 600,
                color: '#1f2937',
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Your collection is empty
              </Typography>
              
              <Typography variant="body1" sx={{
                color: '#6b7280',
                mb: 4,
                maxWidth: '400px',
                lineHeight: 1.6
              }}>
                Start building your collection by adding items with the extension or Plus button.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                      boxShadow: '0 6px 25px rgba(102, 126, 234, 0.5)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => {
                    // You can add navigation to add items functionality here
                  }}
                >
                  Add Items
                </Button>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Empty recommendations message - shown when no recommendations AND not loading */}
        {!isLoadingRecommendations && showRecommendations && (!recommendations || recommendations.length === 0) && (
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, rgba(248,250,252,0.8) 0%, rgba(255,255,255,0.9) 100%)',
              borderRadius: 3,
              border: '2px dashed rgba(156, 163, 175, 0.3)',
              mx: 2
            }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <ViewModuleIcon sx={{ color: 'white', fontSize: 40 }} />
              </Box>
              
              <Typography variant="h5" sx={{
                fontWeight: 600,
                color: '#1f2937',
                mb: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                No recommendations yet
              </Typography>
              
              <Typography variant="body1" sx={{
                color: '#6b7280',
                mb: 4,
                maxWidth: '400px',
                lineHeight: 1.6
              }}>
                {spaceData?.search_for_recommendations 
                  ? "No AI recommendations available yet. Recommendations will appear here as we analyze your collection."
                  : "AI recommendations are not enabled for this space yet. Use the toggle switch above to enable recommendations."
                }
              </Typography>
            </Box>
          </Grid>
        )}
          </Grid>

      <ComparisonDialog />

      {/* Add to Space Dialog */}
      {openSpaceModal && (
        <AddToSpace
          open={openSpaceModal}
          rateId={selectedItemData?.id || selectedItemData?._id}
          objectId={selectedRateId}
          itemData={selectedItemData}
          currentSpaceId={spaceId}
          handleClose={handleSpaceModalClose}
          handleComplete={handleSpaceModalComplete}
          onRefreshRates={() => {
            // Refresh rates after successful operation
            getRatesRequest();
            if (showRecommendations) {
              getRecommendations();
            }
          }}
          authorized={authorized}
        />
      )}

      <ConfirmationDialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onConfirm={() => {
          deleteRate();
          setRemoveDialogOpen(false);
        }}
        title={"Delete rate"}
        cancelButtonString={"Cancel"}
        confirmButtonString={"Delete"}
        message={"Are you sure you want to delete this rate?"}
      />
        </Box>
      </Box>
    </Box>
  );
});

SpaceRates.displayName = 'SpaceRates';

export default SpaceRates;
